import { chatTransport } from "@/src/config/chatTransport.js";
import { transformRequest } from "@/src/utils/chatUtils.js";

export default {
  /* --------------- Group APIs --------------- */
  async fetchGroups(params = {}) {
    try {
      const res = await chatTransport.get("/v1/groups", { params });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching groups: ", error);
      throw error;
    }
  },

  async fetchGroupByID(id) {
    try {
      const res = await chatTransport.get(`/v1/groups/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching group by ID: ", error);
      throw error;
    }
  },

  async fetchUserGroups() {
    try {
      const res = await chatTransport.get("/v1/groups/user/me");
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching user groups: ", error);
      throw error;
    }
  },

  async createGroup(payload) {
    try {
      let res = await chatTransport.post(`/v1/groups`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating a group: ", error);
      throw error;
    }
  },

  async updateGroup(id, payload) {
    try {
      let res = await chatTransport.put(`/v1/groups/${id}`, payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating a group: ", error);
      throw error;
    }
  },

  async fetchGroupByEntity(entityType, entityId) {
    try {
      const res = await chatTransport.get("/v1/groups/by-entity", {
        params: { entity_type: entityType, entity_id: entityId },
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching group by entity: ", error);
      throw error;
    }
  },

  async deleteGroup(id) {
    try {
      const res = await chatTransport.delete(`/v1/groups/${id}`);
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting a group: ", error);
      throw error;
    }
  },

  async togglePinGroup(groupId) {
    try {
      const res = await chatTransport.post(`/v1/groups/${groupId}/pin`, {});
      return res.data;
    } catch (error) {
      console.error("An error occurred while toggling pin: ", error);
      throw error;
    }
  },

  async fetchPinnedGroups() {
    try {
      const res = await chatTransport.get("/v1/groups/pins");
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching pinned groups: ", error);
      throw error;
    }
  },

  async markAsRead(groupId) {
    try {
      const res = await chatTransport.post(`/v1/groups/${groupId}/mark-read`, {});
      return res.data;
    } catch (error) {
      console.error("An error occurred while marking messages as read: ", error);
      throw error;
    }
  },

  /* --------------- Group Member APIs --------------- */
  async addGroupMember(groupID, userId, role = "MEMBER") {
    try {
      const res = await chatTransport.post(
        `/v1/groups/${groupID}/members`,
        {
          user_id: userId,
          role: role,
        },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while adding group member: ", error);
      throw error;
    }
  },

  async removeGroupMember(groupID, userID) {
    try {
      const res = await chatTransport.delete(
        `/v1/groups/${groupID}/members/${userID}`,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while removing group member: ", error);
      throw error;
    }
  },

  async updateMemberRole(groupID, userID, payload) {
    try {
      const res = await chatTransport.put(
        `/v1/groups/${groupID}/members/${userID}/role`,
        payload,
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while updating member role: ", error);
      throw error;
    }
  },

  async fetchGroupMembers(groupID, params = {}) {
    try {
      const res = await chatTransport.get(`/v1/groups/${groupID}/members`, { params });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching group members: ", error);
      throw error;
    }
  },

  /* --------------- Message APIs --------------- */
  async createMessage(payload) {
    try {
      const res = await chatTransport.post("/v1/messages", payload);
      return res.data;
    } catch (error) {
      console.error("An error occurred while creating message: ", error);
      throw error;
    }
  },

  async fetchGroupMessages(groupId, limit = 50, offset = 0, search = null) {
    try {
      const params = { group_id: groupId, limit, offset };
      if (search != null && String(search).trim() !== "") {
        params.search = String(search).trim();
      }
      const transformedParams = transformRequest(params);
      const res = await chatTransport.get("/v1/messages/group", {
        params: transformedParams,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching group messages: ", error);
      throw error;
    }
  },

  _fetchMessageCache: new Map(),

  async fetchMessageById(messageId, groupId) {
    const cacheKey = `${messageId}_${groupId}`;
    const cached = this._fetchMessageCache.get(cacheKey);
    if (cached) return cached;

    const promise = chatTransport.get(`/v1/messages/${messageId}`, {
      params: { group_id: groupId },
    }).then(res => res.data).catch(error => {
      console.error("An error occurred while fetching message by ID: ", error);
      throw error;
    }).finally(() => {
      setTimeout(() => this._fetchMessageCache.delete(cacheKey), 2000);
    });

    this._fetchMessageCache.set(cacheKey, promise);
    return promise;
  },

  async fetchThreadReplies(groupId, threadId, limit = 50, offset = 0) {
    try {
      const transformedParams = transformRequest({
        group_id: groupId,
        thread_id: threadId,
        limit,
        offset,
      });
      const res = await chatTransport.get("/v1/messages/thread-replies", {
        params: transformedParams,
      });
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching thread replies: ", error);
      throw error;
    }
  },

  async editMessage(messageId, groupId, text) {
    try {
      const res = await chatTransport.patch(
        `/v1/messages/${messageId}`,
        { text },
        { params: transformRequest({ group_id: groupId }) },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while editing message: ", error);
      throw error;
    }
  },

  async deleteMessage(messageId, recipientType, recipientId, threadId) {
    try {
      const transformedParams = transformRequest({
        recipient_type: recipientType,
        recipient_id: recipientId,
        thread_id: threadId,
      });
      const res = await chatTransport.delete(
        `/v1/messages/${messageId}`,
        { params: transformedParams },
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while deleting message: ", error);
      throw error;
    }
  },

  /* --------------- Reaction APIs --------------- */
  async linkMessageToTask(messageId, payload) {
    try {
      const res = await chatTransport.post(
        `/v1/messages/${messageId}/link-task`,
        payload,
      );
      return { ...res.data, status: res.status };
    } catch (error) {
      console.error("An error occurred while linking message to task: ", error);
      throw error;
    }
  },

  async addReaction(messageId, groupId, emojiKey) {
    try {
      const res = await chatTransport.post(
        `/v1/messages/${messageId}/reactions`,
        { emoji_key: emojiKey },
        { params: { group_id: groupId } }
      );
      return { ...res.data, status: res.status };
    } catch (error) {
      console.error("An error occurred while adding reaction: ", error);
      throw error;
    }
  },

  async fetchReactionUsers(messageId, groupId, emojiKey) {
    try {
      const res = await chatTransport.get(
        `/v1/messages/reaction-users`,
        { params: { message_id: messageId, group_id: groupId, emoji_key: emojiKey } }
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while fetching reaction users: ", error);
      return null;
    }
  },

  async removeReaction(messageId, groupId, emojiKey) {
    try {
      const encodedEmoji = encodeURIComponent(emojiKey);
      const res = await chatTransport.delete(
        `/v1/messages/${messageId}/reactions/${encodedEmoji}`,
        { params: { group_id: groupId } }
      );
      return res.data;
    } catch (error) {
      console.error("An error occurred while removing reaction: ", error);
      throw error;
    }
  },
};
